import { EChartsOption } from 'echarts-for-react';

import { IChartStackedBarValue, DONUT_COLORS, IChartVisibilityValue } from '@/constants/geo';

import styles from './index.module.less';

const tooltipRound = (color: string) =>
  `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;

const COMMON_EXTRA_CSS_TEXT = `
border-radius: 12px;
border: 1px solid #FFF;
background: rgba(33, 43, 53, 0.40);
box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.10);
backdrop-filter: blur(5px);
color:  #FFF;
padding: 12px;
font-size: 12px ;
`;

// 环形图
export const getDonutOption = (config: { data: number[]; legend: string[]; colors?: string[] }) => {
  const { data, legend, colors = DONUT_COLORS } = config;
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => `
        <div class="${styles['ctc_donut_wrap']}">
          <div class="${styles['ctc_donut_left']}">
            ${params.marker}
            <div>${params.name}</div>
          </div>
          <div>${params.value}%</div>
        </div>`,
      position: function (pos: any, _1: any, _2: any, _3: any, size: any) {
        // 鼠标在左侧时 tooltip 显示到右侧，鼠标在右侧时 tooltip 显示到左侧。
        const obj: any = { top: pos[1] };
        obj[['left', 'right'][+(pos[0] < size.contentSize[0] / 4)]] = 5;
        return obj;
      },
    },
    legend: {
      bottom: 20,
      left: 'center',
      data: legend,
      itemWidth: 8,
      itemHeight: 8,
      textStyle: {
        color: '#666',
        overflow: 'truncate',
        ellipsis: '...', // 超出部分显示省略号
        width: 200,
        fontSize: 10,
      },
      tooltip: { show: true },
      icon: 'circle',
      itemGap: 4,
      padding: 2,
    },
    series: [
      {
        type: 'pie',
        radius: ['70%', '90%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        label: {
          show: false,
        },
        data: data.map((item, index) => ({
          value: item,
          name: legend[index],
          itemStyle: {
            color: colors[index],
          },
        })),
      },
    ],
  };
};

/** 获取平台可见性的 chart 配置 */
export const getVisibilityChartOption = (config: { legend: string[]; data: IChartVisibilityValue[]; isMobile?: boolean }): EChartsOption => {
  const { legend, data, isMobile } = config;
  const orangeGradient = {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: '#ffaf70' },
      { offset: 1, color: '#ff8c00' },
    ],
  };

  const visibilityBarOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      formatter: (params: any) => {
        const param = params[0];
        const value = param?.data?.value || 0;
        const realValue = param?.data?.realValue || 0;
        return `
        <div>
          <div class="${styles['ctc_bar_title']}">${param.name}</div>
          <div class="${styles['ctc_bar_wrap']}">
            <div class="${styles['ctc_bar_left']}">
              ${tooltipRound('#FA8846')}
              <div>${param.seriesName}</div>
            </div>
            <div>${realValue}%（${value}）</div>
          </div>
        </div>`;
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '3%',
      containLabel: true,
    },
    yAxis: {
      type: 'category',
      data: legend,
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      ...(isMobile
        ? {
            axisLabel: {
              interval: 0, // 显示所有标签
              // rotate: 45, // 旋转标签避免重叠
              overflow: 'break', // 文本超出时换行
            },
          }
        : {}),
    },
    xAxis: {
      type: 'value',
      // max: 100,
      axisLabel: {
        // formatter: '{value}%',
        fontFamily: 'DIN',
        rotate: 45,
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
        },
      },
    },
    series: [
      {
        name: '可见性',
        type: 'bar',
        barWidth: '10px',
        data,
        itemStyle: {
          color: orangeGradient,
          opacity: 0.5,
          borderRadius: [5, 5, 5, 5],
        },
        // 悬停状态样式
        emphasis: {
          focus: 'series',
          itemStyle: {
            color: orangeGradient,
            opacity: 1,
          },
        },
      },
    ],
  };
  return visibilityBarOption;
};

// 堆叠柱状图
export const getStackedBarOption = (config: { legend: string[]; data: IChartStackedBarValue[][]; isMobile?: boolean }) => {
  const { legend, data, isMobile } = config;
  const series = data.map((seriesData, index) => {
    return {
      name: seriesData[0]?.label || `series-${index}`,
      type: 'bar',
      stack: 'total',
      barWidth: '10px',
      data: data[index].map((item, i) => {
        const isFirst = index === 0;
        const isLast = index === data.length - 1;
        let borderRadius = [0, 0, 0, 0];
        if (isFirst && isLast) {
          borderRadius = [5, 5, 5, 5];
        }
        if (isFirst && !isLast) {
          borderRadius = [5, 0, 0, 5];
        }
        if (isLast) {
          borderRadius = [0, 5, 5, 0];
        }
        return {
          ...item,
          itemStyle: {
            color: DONUT_COLORS[index],
            opacity: 0.5,
            borderRadius,
          },
        };
      }),
      // 悬停状态样式
      emphasis: {
        focus: 'none',
        itemStyle: {
          opacity: 1,
        },
      },
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: {
        type: 'none',
      },
      extraCssText: COMMON_EXTRA_CSS_TEXT,
      // alwaysShowContent: true, // 关键配置：始终显示提示框
      formatter: (params: any) => {
        const param = params[0];
        let html = `
          <div>
            <div class="${styles['ctc_stacked_bar_title']}">${param.name}</div>
        `;

        params.forEach((el: any, index: number) => {
          const label = el?.data?.label || '';
          const value = el?.data?.value || 0;
          const realValue = el?.data?.realValue || 0;
          if (!label) return '';

          html += `
            <div class="${styles['ctc_stacked_bar_wrap']}">
              <div class="${styles['ctc_stacked_bar_left']}">
                ${tooltipRound(DONUT_COLORS[index])}
                <div>${label}</div>
              </div>
              <div>${realValue}%（${value}）</div>
            </div>
          `;
        });

        html += `
          </div>
        `;
        return html;
      },
    },
    legend: { show: false },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '3%',
      containLabel: true,
    },
    yAxis: {
      type: 'category',
      data: legend,
      axisTick: { show: false },
      axisLine: { show: false },
      ...(isMobile
        ? {
            axisLabel: {
              interval: 0, // 显示所有标签
              // rotate: 45, // 旋转标签避免重叠
              overflow: 'break', // 文本超出时换行
            },
          }
        : {}),
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => {
          return value > 999 ? value / 1000 + 'k' : value;
        },
        fontFamily: 'DIN',
        rotate: 45,
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
        },
      },
    },
    series,
  };
};

// 把环形图getDonutOption和柱状图getStackedBarOption合到一个图里，使用grid合理布局
export const getCombinedDonutStackedBarOption = (config: { donutConfig: any; stackedBarConfig: any }) => {
  const { donutConfig, stackedBarConfig } = config;
  const donutOption = getDonutOption(donutConfig);
  const stackedBarOption = getStackedBarOption(stackedBarConfig);
  // 合并图表配置
  const combinedOption: EChartsOption = {
    tooltip: { ...stackedBarOption.tooltip },
    legend: {
      ...donutOption.legend,
      bottom: 0,
    },
    grid: [
      {
        index: 0,
        left: '5%',
        top: '10%',
      },
      {
        index: 1,
        right: '3%',
        left: '60%',
        top: '10%',
        // width: '50%',
        bottom: '25%',
      },
    ],
    xAxis: [{ ...stackedBarOption.xAxis, gridIndex: 1 }],
    yAxis: [{ ...stackedBarOption.yAxis, gridIndex: 1 }],
    series: [
      {
        ...donutOption.series?.[0],
        center: ['20%', '45%'],
        radius: ['45%', '60%'],
        tooltip: {
          ...donutOption.tooltip,
        },
      },
      ...(stackedBarOption.series || []).map((seriesItem) => ({
        ...seriesItem,
        xAxisIndex: 0,
        yAxisIndex: 0,
      })),
    ],
  };

  return combinedOption;
};
